<template>
  <div :class="{ 'iframe-mode': isInIframe }">
    <v-toolbar flat color="white" class="elevation-1">
      <v-toolbar-title class="text-zt-xl" :class="primaryTextClass">
        <span class="text-gray-600 text-zt-lg font-bold"
          >{{ $t('message.licenseComponent.licenseBox.days') }}
          {{ $t('message.licenseComponent.licenseBox.disponiblesLabel') }}:
          {{
            saldo +
              ' ' +
              (details && details.holidayTypeDescription
                ? details.holidayTypeDescription
                : '')
          }}</span
        >
      </v-toolbar-title>
    </v-toolbar>
    <v-data-table
      :headers="tableHeaders"
      :items="details.balanceOfPeriods"
      :expand="expand"
      item-key="year"
      class="elevation-1"
      :loading="!details.balanceOfPeriods.length"
      :no-data-text="$t('message.datatable.noDataText')"
      hide-actions
    >
      <template v-slot:headers="props">
        <tr>
          <th
            v-for="header in props.headers"
            :key="header.text"
            :class="header.class"
          >
            <span v-if="!header.tooltip" class="text-gray-700">{{ header.text }}</span>
            <span v-else class="d-flex align-center text-gray-700">
              {{ header.text }}
              <zt-tooltip :text="header.tooltip" position="top" class="ml-1 tooltip-wrapper">
                <zt-icon name="info-circle-alt" class="text-gray-700" style="font-size: 14px;" />
              </zt-tooltip>
            </span>
          </th>
        </tr>
      </template>
      <template v-slot:items="props">
        <tr>
          <td
            @click="props.expanded = !props.expanded"
            class="cursor-pointer"
            data-vs-button="selectItem"
          >
            <zt-icon :name="props.expanded ? 'angle-down' : 'angle-right'" />
          </td>
          <td>
            {{ props.item.year }}
          </td>
          <td class="text-zt-base text-zt-dark-500">
            {{ formatDate(props.item.dateFrom) }}
          </td>

          <td class="text-zt-base text-zt-dark-500">
            {{ formatDate(props.item.dateTo) }}
          </td>
          <td v-if="hasLegalColumn" class="text-zt-base text-zt-dark-500">
            {{ props.item.legal || 0 }}
          </td>
          <td v-if="hasTransferredColumn" class="text-zt-base text-zt-dark-500">
            {{ props.item.transferred || 0 }}
          </td>
          <td v-if="hasAgreedColumn" class="text-zt-base text-zt-dark-500">
            {{ props.item.agreed || 0 }}
          </td>
          <td v-if="hasUsedDaysColumn" class="text-zt-base text-zt-dark-500">
            {{ props.item.usedDays || 0 }}
          </td>
          <td class="text-zt-base text-zt-dark-500">
            {{ props.item.days }}
          </td>
          <td v-if="hasExpireColumn" class="text-zt-base text-zt-dark-500">
            {{ props.item.expire || 0 }}
          </td>
          <td v-if="hasPreviewDaysColumn" class="text-zt-base text-zt-dark-500">
            {{ props.item.previewDays || 0 }}
          </td>
        </tr>
      </template>
      <template v-slot:expand="props">
        <v-card flat>
          <v-card-text>
            <span class="font-bold">{{
              $t('message.LicensesManager.search.balanceDetail')
            }}</span>
            <div v-for="item in props.item.details" :key="item.tipo">
              <span>{{ item.type + ': ' + item.days }}</span>
            </div>
          </v-card-text>
        </v-card>
      </template>
    </v-data-table>
  </div>
</template>

<script>
export default {
  inject: ['state', 'mutations'],
  props: {
    saldo: {
      type: Number,
      default: 0
    },
    details: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      historyList: this.history,
      tableData: [],
      expand: false
    }
  },
  computed: {
    hasLegalColumn() {
      return this.hasDataInColumn('legal')
    },
    hasTransferredColumn() {
      return this.hasDataInColumn('transferred')
    },
    hasAgreedColumn() {
      return this.hasDataInColumn('agreed')
    },
    hasUsedDaysColumn() {
      return this.hasDataInColumn('usedDays')
    },
    hasExpireColumn() {
      return this.hasDataInColumn('expire')
    },
    hasPreviewDaysColumn() {
      return this.hasDataInColumn('previewDays')
    },
    isInIframe() {
      return window.self !== window.top
    },
    primaryTextClass() {
      return this.isInIframe ? 'text-iframe-primary' : 'text-zt-primary-200'
    },
    tableHeaders() {
      const baseHeaders = [
        { text: '', sortable: false, width: '40px' },
        { 
          text: this.$t('message.licenseComponent.licenseBox.periodoLabel'),
          value: 'year',
          width: '80px'
        },
        { 
          text: this.$t('message.licenseComponent.licenseBox.inicioLabel'),
          value: 'dateFrom',
          width: '110px'
        },
        { 
          text: this.$t('message.licenseComponent.licenseBox.limiteLabel'),
          value: 'dateTo',
          width: '110px'
        }
      ]
      
      const conditionalHeaders = [
        {
          condition: this.hasLegalColumn,
          config: {
            text: this.$t('message.licenseComponent.licenseBox.legalesLabel'),
            value: 'legal',
            tooltip: this.$t('message.licenseComponent.licenseBox.legalesTooltip'),
            width: '100px'
          }
        },
        {
          condition: this.hasTransferredColumn,
          config: {
            text: this.$t('message.licenseComponent.licenseBox.transferidosLabel'),
            value: 'transferred',
            tooltip: this.$t('message.licenseComponent.licenseBox.transferidosTooltip'),
            width: '130px'
          }
        },
        {
          condition: this.hasAgreedColumn,
          config: {
            text: this.$t('message.licenseComponent.licenseBox.acordadosLabel'),
            value: 'agreed',
            tooltip: this.$t('message.licenseComponent.licenseBox.acordadosTooltip'),
            width: '110px'
          }
        },
        {
          condition: this.hasUsedDaysColumn,
          config: {
            text: this.$t('message.licenseComponent.licenseBox.gozadosLabel'),
            value: 'usedDays',
            tooltip: this.$t('message.licenseComponent.licenseBox.gozadosTooltip'),
            width: '100px'
          }
        }
      ]
      
      const alwaysVisibleHeader = {
        text: this.$t('message.licenseComponent.licenseBox.disponiblesLabel'),
        value: 'days',
        tooltip: this.$t('message.licenseComponent.licenseBox.disponiblesTooltip'),
        width: '120px'
      }
      
      const endConditionalHeaders = [
        {
          condition: this.hasExpireColumn,
          config: {
            text: this.$t('message.licenseComponent.licenseBox.vencenLabel'),
            value: 'expire',
            tooltip: this.$t('message.licenseComponent.licenseBox.vencenTooltip'),
            width: '110px'
          }
        },
        {
          condition: this.hasPreviewDaysColumn,
          config: {
            text: this.$t('message.licenseComponent.licenseBox.diasAAcreditarseLabel'),
            value: 'previewDays',
            tooltip: this.$t('message.licenseComponent.licenseBox.diasAAcreditarseTooltip'),
            width: '150px'
          }
        }
      ]
      
      const headers = [
        ...baseHeaders.map(h => ({ ...h, sortable: false, align: 'left', class: ['column-header'] })),
        ...conditionalHeaders
          .filter(h => h.condition)
          .map(h => ({ ...h.config, sortable: false, align: 'left', class: ['column-header'] })),
        { ...alwaysVisibleHeader, sortable: false, align: 'left', class: ['column-header'] },
        ...endConditionalHeaders
          .filter(h => h.condition)
          .map(h => ({ ...h.config, sortable: false, align: 'left', class: ['column-header'] }))
      ]
      
      return headers
    }
  },
  methods: {
    hasDataInColumn(columnName) {
      return this.details.balanceOfPeriods && 
             this.details.balanceOfPeriods.some(item => {
               const value = item[columnName]
               return value !== null && value !== undefined && value !== 0 && value !== ''
             })
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day}/${month}/${year}`
    }
  }
}
</script>

<style lang="scss" scoped>
.text-iframe-primary {
  color: #2694fc !important;
}

.column-header {
  font-size: 0.875rem !important;
  color: #969696 !important;
}
.order-button {
  outline: none;
}
::v-deep table.v-table {
  thead th {
    padding: 0 10px !important;
    &:first-child {
      padding: 0 0 0 14px !important;
    }
  }
  tbody td {
    padding: 0 10px !important;
    &:first-child {
      padding: 0 0 0 14px !important;
    }
  }
}
</style>
